import { teamHasDialler } from "@sourcewhale/core";
import { capitalize, chartColors, timestampToDuration } from "./utils";

export const REPORT_PROPERTIES = {};

export const augmentEvent = (event) => {
  const groups = REPORT_PROPERTIES.teamGroupsDict[event.sourcerUserId] || [];
  event.groupIds = groups.map((group) => group.groupId);
  event.teamGroups = groups.map((group) => group.groupName);

  if (event.campaignId) {
    const { campaignName, status, campaignType } =
      REPORT_PROPERTIES.campaignDict[event.campaignId] || {};
    event.campaignName = campaignName;
    event.campaignStatus = status;
    event.campaignType = campaignType;
  } else {
    event.campaignName = "No Campaign";
    event.campaignStatus = null;
    event.campaignType = "No Campaign";
  }

  if (event.kind.startsWith("call-")) {
    const OUTCOMES = {
      noAnswer: "No Answer",
      voicemail: "Voicemail",
      notInterested: "Not Interested",
      interested: "Interested",
      booked: "Meeting",
      callBackLater: "Call Back Later",
    };
    event.outcomeName = OUTCOMES[event.outcomeId] || "No Outcome";
    event.stage = { inbound: "Inbound", outbound: "Outbound" }[event.stage];
  }
};

export const selectedReportFiltersArray = [
  { teamGroup: "groupId" },
  { sourcers: "sourcerUserIds" },
  { senders: "senderUserIds" },
  { campaigns: "campaignIds" },
  { campaignType: "campaignType" },
  { projects: "projectIds" },
  { tags: "tagIds" },
  { candidateCountry: "candidateCountry" },
];

export const selectedReportFilters = () => {
  const values = {};
  selectedReportFiltersArray.forEach((item) => {
    values[Object.values(item)] = $(
      `#${Object.keys(item)}Selector`,
    ).selectpicker("val");
  });
  return values;
};

const filteredData = (DATA, period, dataKey) => {
  const getMinMax = (arr) => {
    let min = arr[0];
    let max = arr[0];
    let i = arr.length;

    while (i--) {
      min = Math.min(arr[i], min);
      max = Math.max(arr[i], max);
    }
    return { min, max };
  };

  const dateString = (datetime, overAYear) => {
    const split = datetime.toDateString().split(" ");
    return overAYear
      ? datetime.toDateString()
      : `${split[0]} ${split[1]} ${split[2]}`;
  };

  const unixToFirstWeekDayDate = (overAYear) => (timestamp) => {
    const d = new Date(timestamp * 1000);

    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);

    d.setDate(diff);

    const split = d.toDateString().split(" ");
    const date = overAYear
      ? `${split[1]} ${split[2]} ${split[3]}`
      : `${split[1]} ${split[2]}`;
    return `WC: ${date}`;
  };

  const unixToDayDate = (overAYear) => (timestamp) => {
    const d = new Date(timestamp * 1000);
    return dateString(d, overAYear);
  };

  const unixToMonth = (overAYear) => (timestamp) => {
    const d = new Date(timestamp * 1000);
    const split = d.toDateString().split(" ");
    return overAYear ? `${split[1]} ${split[3]}` : split[1];
  };

  const unixToQuarter = (overAYear) => (timestamp) => {
    const d = new Date(timestamp * 1000);
    const quarter = Math.floor((d.getMonth() + 3) / 3);
    return overAYear ? `Q${quarter} ${d.getFullYear()}` : `Q${quarter}`;
  };

  const unixToYear = () => (timestamp) => {
    const d = new Date(timestamp * 1000);
    return `${d.getFullYear()}`;
  };

  const addEventsToData = (events, stage) => {
    events.forEach((event) => {
      event.stage = stage;
      return DATA.events.includes(event) ? null : DATA.events.push(event);
    });
  };

  if (dataKey === "events") {
    addEventsToData(DATA.initialOutreach, "initialOutreach");
    addEventsToData(DATA.followupOutreach, "followupOutreach");
  }

  const returnData = DATA[dataKey];
  const minMax = getMinMax(returnData.map((x) => x.timestamp));
  const overAYear = minMax.max - minMax.min > 60 * 60 * 24 * 365;

  const mapping = {
    days: unixToDayDate(overAYear),
    weeks: unixToFirstWeekDayDate(overAYear),
    months: unixToMonth(overAYear),
    quarters: unixToQuarter(overAYear),
    years: unixToYear(overAYear),
  };

  const dateFunction = mapping[period];

  returnData.forEach((item) => {
    item.time = dateFunction(item.timestamp);
  });

  const selected = selectedReportFilters();

  return returnData.reduce((ret, x) => {
    if (
      selected.projectIds.length &&
      !(x.projectIds || []).some((p) => selected.projectIds.includes(p))
    )
      return ret;
    if (
      selected.tagIds.length &&
      !(x.tagIds || []).some((t) => selected.tagIds.includes(t))
    )
      return ret;
    if (
      selected.campaignIds.length &&
      !selected.campaignIds.includes(x.campaignId)
    )
      return ret;
    if (
      selected.campaignType.length &&
      !selected.campaignType.includes(x.campaignType)
    )
      return ret;
    if (
      selected.senderUserIds.length &&
      !selected.senderUserIds.includes(x.senderUserId)
    )
      return ret;
    if (
      selected.sourcerUserIds.length &&
      !selected.sourcerUserIds.includes(x.sourcerUserId)
    )
      return ret;
    if (
      selected.candidateCountry.length &&
      !selected.candidateCountry.includes(x.countryCode)
    )
      return ret;
    ret.push(x);
    return ret;
  }, []);
};

const getTableData = (
  DATA,
  key,
  filtered = null,
  breakdownLabels = [],
  breakdownField = null,
) => {
  const period =
    $("#periodSelector").val() || REPORT_PROPERTIES.sharedReportPeriod;
  filtered = filtered || filteredData(DATA, period, "events");
  filtered.sort((a, b) => a.timestamp - b.timestamp);

  const arrayKeys = ["projectNames", "teamGroups"];

  const timestampMap = {};
  const campaignStatusMap = {};
  const userStatusMap = {};

  const mainRowLabels = filtered.reduce((ret, x) => {
    if (arrayKeys.includes(key) && x[key] && Array.isArray(x[key])) {
      x[key].forEach((_, i) => {
        if (x[key][i] !== null && x[key][i] !== undefined) {
          const toPush =
            typeof x[key][i] === "string" ? x[key][i].trim() : x[key][i];
          if (!ret.includes(toPush)) {
            ret.push(toPush);
            timestampMap[toPush] = x.timestamp;
            userStatusMap[toPush] = x.sourcerStatus;
          }
        }
      });
    } else if (x[key] !== null && x[key] !== undefined) {
      const toPush = typeof x[key] === "string" ? x[key].trim() : x[key];
      if (!ret.includes(toPush)) {
        ret.push(toPush);
        timestampMap[toPush] = x.timestamp;
        userStatusMap[toPush] = x.sourcerStatus;
        if (key === "campaignName")
          campaignStatusMap[toPush] = x.campaignStatus;
      }
    }
    return ret;
  }, []);

  const sourcersSelected =
    $("#sourcersSelector").length && $("#sourcersSelector").val().length;

  if (key === "sourcer" && !sourcersSelected) {
    const difference = DATA.allSourcers.filter(
      (x) => !mainRowLabels.includes(x.text?.trim()),
    );
    const missingSourceNames = difference.map((x) => x.text?.trim());
    mainRowLabels.push(...missingSourceNames);
  }

  const rowLabels = [];
  mainRowLabels.forEach((label) => {
    if (!label) return;

    label = label.trim();

    const timestamp = timestampMap[label] || null;
    const status = key === "campaignName" ? campaignStatusMap[label] : null;
    const userStatus = userStatusMap[label] || null;

    rowLabels.push({
      label,
      userStatus,
      timestamp,
      key,
      ...(status && { status }),
    });

    breakdownLabels.forEach((subLabel) => {
      rowLabels.push({
        label,
        subLabel,
        userStatus,
        timestamp,
        key,
        ...(status && { status }),
      });
    });
  });

  const categoryDict = {};

  rowLabels.forEach((rowLabel, ind) => {
    if (rowLabel.subLabel) {
      categoryDict[`${rowLabel.label}::${rowLabel.subLabel}`] = ind;
    } else {
      categoryDict[rowLabel.label] = ind;
    }
  });

  const columnNames = [
    "sourced",
    "sent",
    "opened",
    "replied",
    "interested",
    "booked",
    "notInterested",
    "initialOutreach",
    "followupOutreach",
  ];

  const columns = columnNames.reduce((ret, stage) => {
    ret[stage] = Array.from({ length: rowLabels.length }).fill(0);
    return ret;
  }, {});

  filtered.forEach((event) => {
    if (!columns[event.stage]) return;

    if (arrayKeys.includes(key) && event[key] && Array.isArray(event[key])) {
      event[key].forEach((_, i) => {
        const val = event[key] ? event[key][i].trim() : event[key][i];
        columns[event.stage][categoryDict[val]] += 1;

        const categoryKey = `${val}::${event[breakdownField]}`;
        if (categoryDict[categoryKey] !== undefined) {
          columns[event.stage][categoryDict[categoryKey]] += 1;
        }
      });
    } else {
      const val = event[key] ? event[key].trim() : event[key];
      columns[event.stage][categoryDict[val]] += 1;

      const categoryKey = `${val}::${event[breakdownField]}`;
      if (categoryDict[categoryKey] !== undefined) {
        columns[event.stage][categoryDict[categoryKey]] += 1;
      }
    }
  });

  return { rowLabels, columns };
};

const getCallsTableData = (
  key,
  filtered,
  breakdownLabels = [],
  breakdownField = null,
) => {
  filtered.sort((a, b) => a.timestamp - b.timestamp);

  const arrayKeys = ["projectNames", "teamGroups"];

  const timestampMap = {};
  const campaignStatusMap = {};
  const userStatusMap = {};

  const mainRowLabels = filtered.reduce((ret, x) => {
    if (arrayKeys.includes(key) && x[key] && Array.isArray(x[key])) {
      x[key].forEach((_, i) => {
        if (x[key][i] !== null && x[key][i] !== undefined) {
          const toPush =
            typeof x[key][i] === "string" ? x[key][i].trim() : x[key][i];
          if (!ret.includes(toPush)) {
            ret.push(toPush);
            timestampMap[toPush] = x.timestamp;
            userStatusMap[toPush] =
              key === "caller" ? x.callerStatus : x.sourcerStatus;
          }
        }
      });
    } else if (x[key] !== null && x[key] !== undefined) {
      const toPush = typeof x[key] === "string" ? x[key].trim() : x[key];
      if (!ret.includes(toPush)) {
        ret.push(toPush);
        timestampMap[toPush] = x.timestamp;
        userStatusMap[toPush] =
          key === "caller" ? x.callerStatus : x.sourcerStatus;
        if (key === "campaignName")
          campaignStatusMap[toPush] = x.campaignStatus;
      }
    }
    return ret;
  }, []);

  const rowLabels = [];
  mainRowLabels.forEach((label) => {
    if (!label) return;

    label = label.trim();

    const timestamp = timestampMap[label] || null;
    const status = key === "campaignName" ? campaignStatusMap[label] : null;
    const userStatus = userStatusMap[label] || null;
    rowLabels.push({
      label,
      userStatus,
      timestamp,
      key,
      ...(status && { status }),
    });

    breakdownLabels.forEach((subLabel) => {
      rowLabels.push({
        label,
        subLabel,
        userStatus,
        timestamp,
        key,
        ...(status && { status }),
      });
    });
  });

  const categoryDict = {};

  rowLabels.forEach((rowLabel, ind) => {
    if (rowLabel.subLabel) {
      categoryDict[`${rowLabel.label}::${rowLabel.subLabel}`] = ind;
    } else {
      categoryDict[rowLabel.label] = ind;
    }
  });

  const columnNames = [
    "called",
    "connected",
    "interested",
    "booked",
    "duration",
  ];

  const columns = columnNames.reduce((ret, stage) => {
    ret[stage] = Array.from({ length: rowLabels.length }).fill(0);
    return ret;
  }, {});

  filtered.forEach((call) => {
    if (arrayKeys.includes(key) && call[key] && Array.isArray(call[key])) {
      call[key].forEach((_, i) => {
        const val = call[key] ? call[key][i].trim() : call[key][i];
        const categoryKey = `${val}::${call[breakdownField]}`;
        columnNames.forEach((flag) => {
          if (call[flag] || flag === "called") {
            columns[flag][categoryDict[val]] +=
              flag === "duration" ? call[flag] : 1;

            if (categoryDict[categoryKey] !== undefined) {
              columns[flag][categoryDict[categoryKey]] +=
                flag === "duration" ? call[flag] : 1;
            }
          }
        });
      });
    } else {
      const val = call[key] ? call[key].trim() : call[key];
      const categoryKey = `${val}::${call[breakdownField]}`;

      columnNames.forEach((flag) => {
        if (call[flag] || flag === "called") {
          columns[flag][categoryDict[val]] +=
            flag === "duration" ? call[flag] : 1;

          if (categoryDict[categoryKey] !== undefined) {
            columns[flag][categoryDict[categoryKey]] +=
              flag === "duration" ? call[flag] : 1;
          }
        }
      });
    }
  });

  return { rowLabels, columns };
};

const addColumnHeadTooltip = (column, title) => {
  $(`th.${column}`).attr("data-original-title", title);
};

export const addColumnHeadTooltips = () => {
  $(".table thead th").attr("data-toggle", "tooltip");

  addColumnHeadTooltip(
    "sourced-col-head",
    "The number of contacts who have been added to Sourcewhale or added to a campaign within the given time frame.",
  );

  addColumnHeadTooltip(
    "messaged-col-head",
    'The number of contacts who have moved from "sourced" to "messaged" within the given time frame.',
  );

  addColumnHeadTooltip(
    "opened-col-head",
    "SourceWhale calculates open rates only for emails with open tracking enabled. Campaigns without open tracking or LinkedIn InMail steps that aren't marked as replied or interested won't be included in the opened calculation.",
  );

  addColumnHeadTooltip(
    "replied-col-head",
    "SourceWhale will not include any manual steps in the replied calculation until they are manually marked as replied, interested or not interested, to ensure numbers are not artificially inflated.",
  );

  addColumnHeadTooltip(
    "interested-col-head",
    "SourceWhale AI can identify the sentiment of replies and classify them into one of three categories; interested, not interested and neutral. InMails and other manual steps must be manually marked as interested to be included in the calculation.",
  );

  addColumnHeadTooltip(
    "not-interested-col-head",
    "SourceWhale AI can identify the sentiment of replies and classify them into one of three categories; interested, not interested and neutral. InMails and other manual steps must be manually marked as not interested to be included in the calculation.",
  );

  addColumnHeadTooltip(
    "booked-col-head",
    `If a contact is listed as an attendee in an event in your calendar, SourceWhale's AI will classify them as "meeting."`,
  );

  $('[data-toggle="tooltip"]')
    .tooltip("dispose")
    .tooltip({ html: true, target: "hover" });
};

const calculateBreakdownPercentage = (table, data, type, row, key) => {
  if (type !== "display" || !row.rowLabel.subLabel) {
    return data;
  }

  const dataTable = $(`#${table}`).DataTable();
  const allData = dataTable.rows().data().toArray();

  const groupParentRow = allData.find(
    (rowData) =>
      rowData.rowLabel.label === row.rowLabel.label &&
      !rowData.rowLabel.subLabel,
  );

  if (!groupParentRow) {
    return data;
  }

  const groupParentValue = groupParentRow[key];
  const percentage =
    groupParentRow[key] === 0 ? 0 : ((data / groupParentValue) * 100).toFixed();

  return `${data}<span class="small ml-1">(${percentage}%)</span>`;
};

const subLabelMapping = {
  candidateSourcing: "Candidate Sourcing",
  businessDevelopment: "Business Development",
};

const labelIndexes = {};

const renderRowLabelColumn = (data, type) => {
  if (type === "sort") {
    if (data.key === "time") return data.timestamp;

    const base = data.label;

    if (!labelIndexes[base]) {
      labelIndexes[base] = Object.keys(labelIndexes).length + 1;
    }

    return data.subLabel
      ? `${data.timestamp}_${labelIndexes[base]}.${data.subLabel}`
      : `${data.timestamp}_${labelIndexes[base]}`;
  }

  if (!data.subLabel)
    return (data.key === "sourcer" || data.key === "caller") &&
      data.userStatus === "deleted"
      ? `${data.label} (Deactivated)`
      : data.label;

  data.subLabel = subLabelMapping[data.subLabel] || data.subLabel;

  return `${capitalize(data.subLabel)}`;
};

const setTableRowGrouping = (tableWrapperId, row, data) => {
  const $tableWrapper = $(`#${tableWrapperId}`);
  $tableWrapper.removeClass("grouped");
  if (data.rowLabel.subLabel) {
    const groupName = `group-${data.rowLabel.label}`;
    $(row).addClass(`group ${groupName}`);
    $tableWrapper.addClass(`grouped`);
  }
};

const candidateStatsTable = $("#candidateStatsTable").DataTable({
  buttons: [{ extend: "csv", filename: "Report", extension: ".csv" }],
  columns: [
    {
      data: "rowLabel",
      title: "Grouping",
      render: (data, type) =>
        renderRowLabelColumn(data, type, "candidateStatsTable"),
      className: "label-col-head",
    },
    {
      data: "sourced",
      title: "Sourced",
      className: "sourced-col-head text-center",
      render: (data, type, row) =>
        calculateBreakdownPercentage(
          "candidateStatsTable",
          data,
          type,
          row,
          "sourced",
        ),
    },
    {
      title: "Messaged",
      data: "sent",
      className: "messaged-col-head text-center",
      render: (data, type, row) =>
        calculateBreakdownPercentage(
          "candidateStatsTable",
          data,
          type,
          row,
          "sent",
        ),
    },
    {
      data: (row) => [row.opened],
      title: "Opened",
      className: "opened-col-head text-center",
      render: (data, type, row) =>
        calculateBreakdownPercentage(
          "candidateStatsTable",
          data,
          type,
          row,
          "opened",
        ),
    },
    {
      data: (row) => [row.replied],
      title: "Replied",
      className: "replied-col-head text-center",
      render: (data, type, row) =>
        calculateBreakdownPercentage(
          "candidateStatsTable",
          data,
          type,
          row,
          "replied",
        ),
    },
    {
      data: (row) => [row.interested],
      title: "Interested",
      className: "interested-col-head text-center",
      render: (data, type, row) =>
        calculateBreakdownPercentage(
          "candidateStatsTable",
          data,
          type,
          row,
          "interested",
        ),
    },
    {
      data: (row) => [row.notInterested],
      title: "Not Interested",
      className: "not-interested-col-head text-center",
      render: (data, type, row) =>
        calculateBreakdownPercentage(
          "candidateStatsTable",
          data,
          type,
          row,
          "notInterested",
        ),
    },
    {
      data: (row) => [row.booked],
      title: "Meeting",
      className: "booked-col-head text-center",
      render: (data, type, row) =>
        calculateBreakdownPercentage(
          "candidateStatsTable",
          data,
          type,
          row,
          "booked",
        ),
    },
  ],
  rowCallback: (row, data) => {
    const { rowLabel } = data;
    if (rowLabel.status)
      $(row).addClass("campaign-row").addClass(`${rowLabel.status}`);
    if (rowLabel.key === "sourcer" && rowLabel.userStatus === "deleted")
      $(row).addClass("deleted-sourcer");
  },
  searching: false,
  paging: false,
  deferRender: true,
  info: false,
  bAutoWidth: false,
  scrollY: "100vh",
  scrollCollapse: true,
  order: [[0, "asc"]],
  createdRow: (row, data) =>
    setTableRowGrouping("candidateStatsTable_wrapper", row, data),
});

const teamActivityTable = $("#teamActivityTable").DataTable({
  buttons: [{ extend: "csv", filename: "Report", extension: ".csv" }],
  columns: [
    {
      data: "rowLabel",
      title: "Teammate",
      render: (data, type) =>
        renderRowLabelColumn(data, type, "teamActivityTable"),
      className: "label-col-head",
    },
    {
      data: (row) => [row.sourced],
      title: "Sourced",
      className: "sourced-col-head text-center",
    },
    {
      data: (row) => [row.initialOutreach],
      title: "First Messages",
      className: "first-message-col-head text-center",
    },
    {
      data: (row) => [row.followupOutreach],
      title: "Follow-up Messages",
      className: "followup-message-col-head text-center",
    },
    {
      data: (row) => [row.replied],
      title: "Replied",
      className: "replied-col-head text-center",
    },
    {
      data: (row) => [row.interested],
      title: "Interested",
      className: "interested-col-head text-center",
    },
    {
      data: (row) => [row.booked],
      title: "Meeting",
      className: "booked-col-head text-center",
    },
  ],
  rowCallback: (row, data) => {
    const { rowLabel } = data;
    if (rowLabel.status)
      $(row).addClass("campaign-row").addClass(`${rowLabel.status}`);
    if (rowLabel.key === "sourcer" && rowLabel.userStatus === "deleted")
      $(row).addClass("deleted-sourcer");
  },
  searching: true,
  paging: false,
  deferRender: true,
  dom: "t",
  bAutoWidth: false,
  scrollY: "100vh",
  scrollCollapse: true,
  order: [[0, "asc"]],
  createdRow: (row, data) =>
    setTableRowGrouping("teamActivityTable_wrapper", row, data),
});

const diallerActivityTable = $("#diallerActivityTable").DataTable({
  buttons: [{ extend: "csv", filename: "Report", extension: ".csv" }],
  columns: [
    {
      data: "rowLabel",
      title: "Grouping",
      render: (data, type) =>
        renderRowLabelColumn(data, type, "diallerActivityTable"),
      className: "label-col-head",
    },
    {
      data: "called",
      title: "Calls",
      className: "called-col-head text-center",
      render: (data, type, row) =>
        calculateBreakdownPercentage(
          "diallerActivityTable",
          data,
          type,
          row,
          "called",
        ),
    },
    {
      data: (row) => row,
      title: "",
      className: "connected-rate-col-head text-center",
      orderable: false,
      render: (data) => {
        const { called, connected } = data;
        const p = called === 0 ? 0 : ((connected / called) * 100).toFixed(0);
        return `<div class="text-gray-500"><i class="fa-solid fa-arrow-right mr-2"></i>${p}%<i class="ml-2 fa-solid fa-arrow-right"></i></div>`;
      },
    },
    {
      data: "connected",
      title: "Connected",
      className: "connected-col-head text-center",
      render: (data, type, row) =>
        calculateBreakdownPercentage(
          "diallerActivityTable",
          data,
          type,
          row,
          "connected",
        ),
    },
    {
      data: (row) => row,
      title: "",
      className: "interested-rate-col-head text-center",
      orderable: false,
      render: (data) => {
        const { connected, interested } = data;
        const p =
          connected === 0 ? 0 : ((interested / connected) * 100).toFixed(0);
        return `<div class="text-gray-500"><i class="fa-solid fa-arrow-right mr-2"></i>${p}%<i class="ml-2 fa-solid fa-arrow-right"></i></div>`;
      },
    },
    {
      data: "interested",
      title: "Interested",
      className: "interested-col-head text-center",
      render: (data, type, row) =>
        calculateBreakdownPercentage(
          "diallerActivityTable",
          data,
          type,
          row,
          "interested",
        ),
    },
    {
      data: (row) => row,
      title: "",
      className: "booked-rate-col-head text-center",
      orderable: false,
      render: (data) => {
        const { interested, booked } = data;
        const p =
          interested === 0 ? 0 : ((booked / interested) * 100).toFixed(0);
        return `<div class="text-gray-500"><i class="fa-solid fa-arrow-right mr-2"></i>${p}%<i class="ml-2 fa-solid fa-arrow-right"></i></div>`;
      },
    },
    {
      data: "booked",
      title: "Meeting",
      className: "booked-col-head text-center",
      render: (data, type, row) =>
        calculateBreakdownPercentage(
          "diallerActivityTable",
          data,
          type,
          row,
          "booked",
        ),
    },
    {
      data: (row) => row,
      title: "Average Time",
      className: "duration-col-head text-center",
      render: (data, type) => {
        // Return raw numeric value for sorting, else return formatted duration
        const duration = data.duration || 0;
        const prop = data.called ? duration / data.called : 0;
        return type === "sort" || type === "type"
          ? prop
          : timestampToDuration(prop);
      },
    },
    {
      data: (row) => row,
      title: "Total Time",
      className: "duration-col-head text-center",
      render: (data, type) => {
        // Return raw numeric value for sorting, else return formatted duration
        const duration = data.duration || 0;
        return type === "sort" || type === "type"
          ? duration
          : timestampToDuration(data.duration);
      },
    },
  ],
  rowCallback: (row, data) => {
    const { rowLabel } = data;
    if (rowLabel.status)
      $(row).addClass("campaign-row").addClass(`${rowLabel.status}`);
    if (
      (rowLabel.key === "sourcer" || rowLabel.key === "caller") &&
      rowLabel.userStatus === "deleted"
    )
      $(row).addClass("deleted-sourcer");
  },
  searching: true,
  paging: false,
  deferRender: true,
  dom: "t",
  bAutoWidth: false,
  scrollY: "100vh",
  scrollCollapse: true,
  order: [[0, "asc"]],
  createdRow: (row, data) =>
    setTableRowGrouping("diallerActivityTable_wrapper", row, data),
});

export const setRowVisibilities = (tableId, campaignStatuses) => {
  $(`${tableId} .campaign-row`).hide();
  campaignStatuses.forEach((status) => {
    $(`${tableId} .campaign-row.${status}`).show();
  });
};

const chartSeriesOptions = (chartType, counts) => {
  if (chartType === "candidateChart") {
    return [
      { name: "Sourced", data: counts.sourced },
      { name: "Messaged", data: counts.sent },
      { name: "Opened", data: counts.opened },
      { name: "Replied", data: counts.replied },
      { name: "Interested", data: counts.interested },
      { name: "Meeting", data: counts.booked },
      { name: "Not Interested", data: counts.notInterested },
    ];
  }
  if (chartType === "teamChart") {
    return [
      { name: "Sourced", data: counts.sourced },
      { name: "First Messages", data: counts.initialOutreach },
      { name: "Follow-up Messages", data: counts.followupOutreach },
      { name: "Replied", data: counts.replied },
      { name: "Interested", data: counts.interested },
      { name: "Meeting", data: counts.booked },
    ];
  }

  if (chartType === "diallerChart") {
    return [
      { name: "Called", data: counts.called },
      { name: "Connected", data: counts.connected },
      { name: "Interested", data: counts.interested },
      { name: "Meeting", data: counts.booked },
    ];
  }

  return "";
};

const chartOptions = (chartType, columns, categories = []) => ({
  series: chartSeriesOptions(chartType, columns) || [],
  colors: chartColors,
  chart: {
    type: "bar",
    width: "100%",
    height: 350,
    toolbar: { show: false },
  },
  plotOptions: {
    bar: { horizontal: false, columnWidth: "55%", endingShape: "rounded" },
  },
  dataLabels: { enabled: false },
  stroke: { show: true, width: 2, colors: ["transparent"] },
  xaxis: {
    categories,
    tickPlacement: "between",
    max: "dataMax",
    min: "dataMin",
  },
  yaxis: {
    labels: {
      formatter: (val) => val.toFixed(0),
    },
  },
  fill: { opacity: 1 },
  legend: { position: "right" },
});

const candidateChart = new ApexCharts($("#candidateChart")[0], chartOptions());

const teamChart = new ApexCharts($("#teamChart")[0], chartOptions());

export const displayCandidateChart = (DATA) => {
  const timeData = getTableData(DATA, "time");
  const categories = timeData.rowLabels.map((rowLabel) => rowLabel.label);
  candidateChart.updateOptions(
    chartOptions("candidateChart", timeData.columns, categories),
  );
};

export const displayTeamChart = (DATA) => {
  const period =
    $("#periodSelector").val() || REPORT_PROPERTIES.sharedReportPeriod;
  const timeData = getTableData(
    DATA,
    "time",
    filteredData(DATA, period, "events"),
  );

  const categories = timeData.rowLabels.map((rowLabel) => rowLabel.label);

  teamChart.updateOptions(
    chartOptions("teamChart", timeData.columns, categories),
  );
};

export const displayCallsFunnel = (DATA) => {
  const period =
    $("#periodSelector").val() || REPORT_PROPERTIES.sharedReportPeriod;
  const timeData = getCallsTableData(
    "time",
    filteredData(DATA, period, "calls"),
  );
  $(`.funnel`).empty().removeClass("svg-funnel-js");

  // eslint-disable-next-line no-undef
  const graph = new FunnelGraph({
    container: ".funnel",
    gradientDirection: "horizontal",
    data: {
      labels: ["Calls", "Connected", "Interested", "Meeting"],
      colors: ["#625df5", "#3365ff", "#1CA8FF", "#ADE7FF"],
      values: [
        timeData.columns.called.reduce((total, val) => total + val, 0),
        timeData.columns.connected.reduce((total, val) => total + val, 0),
        timeData.columns.interested.reduce((total, val) => total + val, 0),
        timeData.columns.booked.reduce((total, val) => total + val, 0),
      ],
    },
    displayPercent: false,
    direction: "horizontal",
    width: $(`.tab-pane.active .chartCard`).width(),
    height: 300,
  });

  graph.draw();

  $(window).on("resize", () => {
    graph.updateWidth($(`.tab-pane.active .chartCard`).width());
  });
};

export const displayCandidateTable = (
  DATA,
  groupBy = "time",
  breakdownField = "none",
) => {
  $("#candidateTableCard").addClass("skeleton-loading");

  const period =
    $("#periodSelector").val() || REPORT_PROPERTIES.sharedReportPeriod;
  const filtered = filteredData(DATA, period, "events");

  const breakdownLabels = new Set();

  breakdownField =
    $("#candidateStatsBreakdownSelector").val() || breakdownField;
  if (breakdownField !== "none") {
    filtered.forEach((event) => {
      if (event[breakdownField]) breakdownLabels.add(event[breakdownField]);
    });
  }
  const rows = [];

  groupBy = $("#candidateStatsGroupBySelector").val() || groupBy;

  const setCampaignVisibilitySelector = groupBy === "campaignName";
  $("#candidateStatsCampaignVisibilitySelectorWrapper").toggle(
    setCampaignVisibilitySelector,
  );

  const tableData = getTableData(
    DATA,
    groupBy,
    filtered,
    Array.from(breakdownLabels),
    breakdownField,
  );

  const keepRow = (r) => {
    const check = [
      "sourced",
      "sent",
      "opened",
      "replied",
      "interested",
      "booked",
      "notInterested",
    ];
    return Object.entries(r).some((x) => check.includes(x[0]) && x[1] > 0);
  };

  tableData.rowLabels.forEach((rowLabel, ind) => {
    const row = { rowLabel };
    Object.entries(tableData.columns).forEach(([stage, stageCounts]) => {
      row[stage] = stageCounts[ind];
    });
    if (keepRow(row)) rows.push(row);
  });

  candidateStatsTable
    .clear()
    .rows.add(rows)
    .order([[0, "asc"]])
    .draw(false);

  const statuses =
    $(`#candidateStatsCampaignVisibilitySelector`).val() ||
    REPORT_PROPERTIES.campaignStatuses;
  setRowVisibilities(`#candidateStatsTable`, statuses);

  $("#candidateTableCard").removeClass("skeleton-loading");
};

export const displayTeamActivityTable = (DATA, breakdownField = "none") => {
  $("#teamActivityTableCard").addClass("skeleton-loading");

  const period =
    $("#periodSelector").val() || REPORT_PROPERTIES.sharedReportPeriod;
  const filtered = filteredData(DATA, period, "events");
  const breakdownLabels = new Set();

  breakdownField = $("#teamActivityBreakdownSelector").val() || breakdownField;
  if (breakdownField !== "none") {
    filtered.forEach((event) => {
      if (event[breakdownField]) breakdownLabels.add(event[breakdownField]);
    });
  }

  const rows = [];

  const tableData = getTableData(
    DATA,
    "sourcer",
    filtered,
    Array.from(breakdownLabels),
    breakdownField,
  );

  tableData.rowLabels.forEach((rowLabel, ind) => {
    const row = { rowLabel };
    Object.entries(tableData.columns).forEach(([stage, stageCounts]) => {
      row[stage] = stageCounts[ind];
    });
    rows.push(row);
  });

  teamActivityTable
    .clear()
    .rows.add(rows)
    .order([[0, "asc"]])
    .draw(false);

  $("#teamActivityTableCard").removeClass("skeleton-loading");
};

export const displayDiallerActivityTable = (
  DATA,
  groupBy = "time",
  breakdownField = "none",
) => {
  $("#diallerActivityTableCard").addClass("skeleton-loading");

  const period =
    $("#periodSelector").val() || REPORT_PROPERTIES.sharedReportPeriod;
  const filtered = filteredData(DATA, period, "calls");
  const breakdownLabels = new Set();

  breakdownField =
    $("#diallerActivityBreakdownSelector").val() || breakdownField;
  if (breakdownField !== "none") {
    filtered.forEach((event) => {
      if (event[breakdownField]) breakdownLabels.add(event[breakdownField]);
    });
  }

  groupBy = $("#diallerActivityGroupBySelector").val() || groupBy;

  const setCampaignVisibilitySelector = groupBy === "campaignName";
  $("#diallerActivityCampaignVisibilitySelectorWrapper").toggle(
    setCampaignVisibilitySelector,
  );

  const rows = [];

  const tableData = getCallsTableData(
    groupBy,
    filtered,
    Array.from(breakdownLabels),
    breakdownField,
  );

  tableData.rowLabels.forEach((rowLabel, ind) => {
    const row = { rowLabel };
    Object.entries(tableData.columns).forEach(([stage, stageCounts]) => {
      row[stage] = stageCounts[ind];
    });
    rows.push(row);
  });

  diallerActivityTable
    .clear()
    .rows.add(rows)
    .order([[0, "asc"]])
    .draw(false);

  const statuses =
    $(`#diallerActivityCampaignVisibilitySelector`).val() ||
    REPORT_PROPERTIES.diallerCampaignStatuses;
  setRowVisibilities(`#diallerActivityTable`, statuses);

  $("#diallerActivityTableCard").removeClass("skeleton-loading");
};

export const initializeReportUtils = () => {
  if (teamHasDialler(false)) {
    $("#diallerActivityNavItem").show();
    $("#shareReportTabSelector").append(
      '<option value="diallerActivity" data-content="Dialer Activity">Dialer Activity</option>',
    );
  }

  [candidateChart, teamChart].forEach((chart) => chart.render());

  $("#teamActivityTableInput").on("keyup", (e) => {
    const searchQuery = $(e.currentTarget).val();
    teamActivityTable.columns(0).search(searchQuery).draw();
  });

  $("#contactActivityTab").on("shown.bs.tab", () => {
    candidateStatsTable.columns.adjust().draw();
  });

  $("#teamActivityTab").on("shown.bs.tab", () => {
    teamActivityTable.columns.adjust().draw();
  });

  $("#diallerActivityTab").on("shown.bs.tab", () => {
    diallerActivityTable.columns.adjust().draw();
  });
};
